/* eslint-disable indent */
import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  HistoryAdvancedFilter,
  EndpointHistoryLocalStorage,
  HistoryType,
  InspectionUsageLocalStorage,
  VehiculeCheckLocalStorage,
} from '@/entities/history';
import { useLocalStorageState } from '../local-storage.hooks';
import { Dayjs } from 'dayjs';

interface HistoryFilterProps {
  historyType: HistoryType;
  setCurrentPage: (page: number) => void;
}

export const useHistoryFilter = ({
  historyType,
  setCurrentPage,
}: HistoryFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] = useState<HistoryAdvancedFilter>(
    { key: '' },
  );
  const filtersEmptyRef = useRef<boolean>(true);

  const getStorageKey = () => {
    let key = '';
    let startDate = '';
    let endDate = '';
    let groupId = '';
    let companyId = '';
    let branchId = '';
    let clientType = '';
    let clientFullName = '';
    let progressStatus = '';
    let humanModerationStatus = '';
    let reportValidated = '';

    switch (historyType) {
      case HistoryType.ENDPOINT_HISTORY:
        key = EndpointHistoryLocalStorage.KEY;
        startDate = EndpointHistoryLocalStorage.START_DATE;
        endDate = EndpointHistoryLocalStorage.END_DATE;
        groupId = EndpointHistoryLocalStorage.GROUP_ID;
        companyId = EndpointHistoryLocalStorage.COMPANY_ID;
        branchId = EndpointHistoryLocalStorage.BRANCH_ID;
        clientType = EndpointHistoryLocalStorage.CLIENT_TYPE;
        clientFullName = EndpointHistoryLocalStorage.CLIENT_FULLNAME;
        progressStatus = EndpointHistoryLocalStorage.PROGRESS_STATUS;
        humanModerationStatus =
          EndpointHistoryLocalStorage.HUMAN_MODERATION_STATUS;
        reportValidated = EndpointHistoryLocalStorage.REPORT_VALIDATED;
        break;
      case HistoryType.VEHICULE_CHECK:
        key = VehiculeCheckLocalStorage.KEY;
        startDate = VehiculeCheckLocalStorage.START_DATE;
        endDate = VehiculeCheckLocalStorage.END_DATE;
        groupId = VehiculeCheckLocalStorage.GROUP_ID;
        companyId = VehiculeCheckLocalStorage.COMPANY_ID;
        branchId = VehiculeCheckLocalStorage.BRANCH_ID;
        clientType = VehiculeCheckLocalStorage.CLIENT_TYPE;
        clientFullName = VehiculeCheckLocalStorage.CLIENT_FULLNAME;
        progressStatus = VehiculeCheckLocalStorage.PROGRESS_STATUS;
        humanModerationStatus =
          VehiculeCheckLocalStorage.HUMAN_MODERATION_STATUS;
        reportValidated = VehiculeCheckLocalStorage.REPORT_VALIDATED;
        break;
      case HistoryType.INSPECTION_USAGE:
        key = InspectionUsageLocalStorage.KEY;
        startDate = InspectionUsageLocalStorage.START_DATE;
        endDate = InspectionUsageLocalStorage.END_DATE;
        groupId = InspectionUsageLocalStorage.GROUP_ID;
        companyId = InspectionUsageLocalStorage.COMPANY_ID;
        branchId = InspectionUsageLocalStorage.BRANCH_ID;
        clientType = InspectionUsageLocalStorage.CLIENT_TYPE;
        clientFullName = InspectionUsageLocalStorage.CLIENT_FULLNAME;
        progressStatus = InspectionUsageLocalStorage.PROGRESS_STATUS;
        humanModerationStatus =
          InspectionUsageLocalStorage.HUMAN_MODERATION_STATUS;
        reportValidated = InspectionUsageLocalStorage.REPORT_VALIDATED;
        break;
      default:
        key = '';
        startDate = '';
        endDate = '';
        groupId = '';
        companyId = '';
        branchId = '';
        clientType = '';
        clientFullName = '';
        progressStatus = '';
        humanModerationStatus = '';
        reportValidated = '';
    }

    return {
      key,
      startDate,
      endDate,
      groupId,
      companyId,
      branchId,
      clientType,
      clientFullName,
      progressStatus,
      humanModerationStatus,
      reportValidated,
    };
  };

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: getStorageKey()?.key,
    removeOnClose: true,
  });

  const [startDate, setStartDate] = useLocalStorageState({
    key: getStorageKey()?.startDate,
    removeOnClose: true,
  });

  const [endDate, setEndDate] = useLocalStorageState({
    key: getStorageKey()?.endDate,
    removeOnClose: true,
  });

  const [groupId, setGroupId] = useLocalStorageState({
    key: getStorageKey()?.groupId,
    removeOnClose: true,
  });

  const [companyId, setCompanyId] = useLocalStorageState({
    key: getStorageKey()?.companyId,
    removeOnClose: true,
  });

  const [branchId, setBranchId] = useLocalStorageState({
    key: getStorageKey()?.branchId,
    removeOnClose: true,
  });

  const [clientType, setClientType] = useLocalStorageState({
    key: getStorageKey()?.clientType,
    removeOnClose: true,
  });

  const [clientFullName, setClientFullName] = useLocalStorageState({
    key: getStorageKey()?.clientFullName,
    removeOnClose: true,
  });

  const [progressStatus, setProgressStatus] = useLocalStorageState({
    key: getStorageKey()?.progressStatus,
    removeOnClose: true,
  });

  const [humanModerationStatus, setHumanModerationStatus] =
    useLocalStorageState({
      key: getStorageKey()?.humanModerationStatus,
      removeOnClose: true,
    });

  const [reportValidated, setReportValidated] = useLocalStorageState({
    key: getStorageKey()?.reportValidated,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      key: key || '',
      startDate,
      endDate,
      groupId,
      companyId,
      branchId,
      clientType,
      clientFullName,
      progressStatus,
      humanModerationStatus,
      reportValidated,
    }));

    filtersEmptyRef.current =
      !key &&
      !startDate &&
      !endDate &&
      !groupId &&
      !companyId &&
      !branchId &&
      !clientType &&
      !clientFullName &&
      !progressStatus &&
      !humanModerationStatus &&
      !reportValidated;
  }, [
    key,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    clientType,
    clientFullName,
    progressStatus,
    humanModerationStatus,
    reportValidated,
  ]);

  const searchByKey = (value: string) => {
    setKey(value);
    setCurrentPage(1);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchStartDate = (value: Dayjs) => {
    setStartDate(value?.format('YYYY-MM-DD'));
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({
      ...prev,
      startDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchEndDate = (value: Dayjs) => {
    setEndDate(value?.format('YYYY-MM-DD'));
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({
      ...prev,
      endDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupId(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, groupId: e.target.value }));
  };

  const searchByCompany = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, companyId: e.target.value }));
  };

  const searchByBranch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBranchId(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, branchId: e.target.value }));
  };

  const searchByClientType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setClientType(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, clientType: e.target.value }));
  };

  const searchByClient = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setClientFullName(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, clientFullName: e.target.value }));
  };

  const searchByProgressStatus = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProgressStatus(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, progressStatus: e.target.value }));
  };

  const searchByHumanModerationStatus = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setHumanModerationStatus(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({
      ...prev,
      humanModerationStatus: e.target.value,
    }));
  };

  const searchByReportValidated = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setReportValidated(e.target.value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({
      ...prev,
      reportValidated: e.target.value,
    }));
  };

  const clearFilters = () => {
    setKey('');
    setStartDate('');
    setEndDate('');
    setGroupId('');
    setCompanyId('');
    setBranchId('');
    setClientType('');
    setClientFullName('');
    setProgressStatus('');
    setHumanModerationStatus('');
    setReportValidated('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    clientType,
    clientFullName,
    progressStatus,
    humanModerationStatus,
    reportValidated,
    searchByKey,
    searchStartDate,
    searchEndDate,
    searchByGroup,
    searchByCompany,
    searchByBranch,
    searchByClientType,
    searchByClient,
    searchByProgressStatus,
    searchByHumanModerationStatus,
    searchByReportValidated,
  };
};
